import React from "react";
import { Phone, Mail, MapPin, ShieldCheck } from "lucide-react";
import { COMPANY_INFO } from "../data";
import ContactForm from "../components/ContactForm";

export default function Contact() {
  return (
    <div className="pt-[72px] sm:pt-[128px]">
      {/* Hero Section */}
      <section className="relative bg-slate-950 py-24 sm:py-32 overflow-hidden text-center">
        <div className="absolute inset-0 z-0">
          <img
            src="https://assets.auxodev.dk/assets/66f352d5-c4b9-4e8b-8d46-eb7bb9ac1d49/ebf1a478-7590-4d02-92a6-2e357d63c109.jpg"
            alt="Kontakt Skræp Vinduespuds"
            className="w-full h-full object-cover opacity-25"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950/90 via-slate-950/75 to-slate-950" />
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold bg-sky-500/10 text-sky-400 border border-sky-500/20 mb-6 uppercase tracking-wider">
            Uforpligtende Tilbud
          </span>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-white tracking-tight uppercase">
            Kontakt Mig
          </h1>
          <p className="text-lg text-slate-300 mt-4 max-w-2xl mx-auto leading-relaxed">
            Har du spørgsmål, eller ønsker du et uforpligtende tilbud på vinduespudsning, solcellerens, algerens eller rengøring af campingvogn? Jeg sidder klar til at hjælpe dig.
          </p>
        </div>
      </section>

      {/* Main Contact Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-start">
            {/* Contact Info Column */}
            <div className="lg:col-span-5 space-y-6">
              <span className="text-sky-500 font-bold text-xs uppercase tracking-wider">
                Kontaktoplysninger
              </span>
              <h2 className="text-3xl font-extrabold text-slate-900 tracking-tight uppercase">
                Lad mig hjælpe dig med din opgave
              </h2>
              <p className="text-slate-600 text-base leading-relaxed mb-10">
                Jeg besvarer alle henvendelser hurtigt. Uanset om du foretrækker at ringe, sende en e-mail eller udfylde min formular, kan du forvente et professionelt og hurtigt svar.
              </p>

              {/* Info Cards */}
              <div className="space-y-6">
                {/* Phone */}
                {COMPANY_INFO.phone && (
                  <a
                    href={`tel:${COMPANY_INFO.phone.replace(/\s+/g, "")}`}
                    className="flex items-start gap-4 p-5 bg-slate-50 rounded-md border border-slate-100 hover:shadow-md transition-shadow group"
                  >
                    <div className="bg-slate-900 text-white p-3.5 rounded-md group-hover:bg-sky-500 transition-colors shadow-sm">
                      <Phone className="h-6 w-6" />
                    </div>
                    <div>
                      <h3 className="font-bold text-slate-900 text-base uppercase tracking-wide">Ring til mig</h3>
                      <p className="text-lg font-extrabold text-sky-500 mt-0.5">{COMPANY_INFO.phone}</p>
                      <p className="text-xs text-slate-500 mt-1">Mandag - Fredag: 07:00 - 17:00</p>
                    </div>
                  </a>
                )}

                {/* Email */}
                <a
                  href={`mailto:${COMPANY_INFO.email}`}
                  className="flex items-start gap-4 p-5 bg-slate-50 rounded-md border border-slate-100 hover:shadow-md transition-shadow group"
                >
                  <div className="bg-slate-900 text-white p-3.5 rounded-md group-hover:bg-sky-500 transition-colors shadow-sm">
                    <Mail className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-900 text-base uppercase tracking-wide">Send en e-mail</h3>
                    <p className="text-slate-700 font-semibold mt-0.5 break-all">{COMPANY_INFO.email}</p>
                    <p className="text-xs text-slate-500 mt-1">Jeg svarer inden for få timer</p>
                  </div>
                </a>

                {/* Area */}
                <div className="flex items-start gap-4 p-5 bg-slate-50 rounded-md border border-slate-100">
                  <div className="bg-slate-900 text-white p-3.5 rounded-md shadow-sm">
                    <MapPin className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-900 text-base uppercase tracking-wide">Dækningsområde</h3>
                    <p className="text-slate-700 font-semibold mt-0.5">{COMPANY_INFO.geoArea}</p>
                    <p className="text-xs text-slate-500 mt-1">{COMPANY_INFO.address}</p>
                  </div>
                </div>
              </div>

              {/* Trust Badge */}
              <div className="mt-10 flex items-center gap-3 bg-sky-50/50 border border-sky-100 p-4 rounded-md">
                <ShieldCheck className="h-6 w-6 text-sky-500 shrink-0" />
                <p className="text-sm text-slate-700 font-medium">
                  Jeg er fuldt ansvarsforsikret. Dine ejendele og dit hjem er i trygge hænder hos mig.
                </p>
              </div>
            </div>

            {/* Form Column */}
            <div className="lg:col-span-7">
              <ContactForm variant="white" />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
